import { useMutation, useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import message from 'antd/es/message';
import ReactGA4 from 'react-ga4';

import { request, L2TxReqParams, logout, toastError } from 'utils';
import { updateUserDetailQuery, useUpdateCurrentAddressInfo } from 'hooks';

export const getEmailCode = (email: string) =>
  request.post<{ is_success: boolean }>('/common/send_email_code', {
    category: 'bind_email',
    email,
  });

export const getSignText = (action: string, content?: string) =>
  request
    .get<{ text: string }>('/user/signature', content ? { action, content } : { action })
    .then(data => data?.text);

export const useLogout = () => {
  const history = useHistory();

  const mutation = useMutation(() => request.get('/user/logout'), {
    onSuccess() {
      logout();

      ReactGA4.event({
        category: 'Connected',
        action: 'Logout',
      });
      setTimeout(() => {
        history.replace('/');
      });
    },
    onError(err: string) {
      toastError(err);
    },
  });

  return mutation;
};

type UserConfigRes = {
  user_account_limit: number;
  out_of_limit: number;
  total_minted_id_num: number;
  user_paid_mint_price: number;
};

export const useUserSetting = () => {
  const url = '/user/setting';

  const query = useQuery([url], () => request.get<UserConfigRes>(url));

  return query;
};

export const useMintWeb3Id = (address?: string) => {
  const update = useUpdateCurrentAddressInfo();

  const res = useMutation(
    ({
      web3_id,
      tx,
    }: {
      web3_id: string;
      // only check the id is avaliable
      tx?: L2TxReqParams;
    }) =>
      request.post<{ is_success: boolean }>('/user/mint_web3_id', {
        web3_id,
        tx,
      }),
    {
      onSuccess(res, { web3_id }) {
        if (!res.is_success) {
          return;
        }
        updateUserDetailQuery({
          id: address!,
          account_id: web3_id,
        });

        update(pre => {
          const next = { ...pre };
          if (next.detail) {
            next.detail = {
              ...next.detail,
              account_id: web3_id,
            };
          }

          if (next.userInfo?.desc) {
            next.userInfo = {
              ...next.userInfo,
              web3_id,
              desc: {
                ...next.userInfo.desc,
                user: {
                  ...next.userInfo.desc.user,
                  web3_id,
                },
              },
            };
          }

          return next;
        });

        message.success('saved');
      },
      onError(err) {
        toastError(err);
      },
    },
  );

  return res;
};

export const useCheckWeb3IdExist = () => {
  const res = useMutation((params: { web3_id: string }) =>
    request.get<{ is_exists: boolean }>('/user/web3_id_exists', params),
  );

  return res;
};

export const useOrgApplyPost = () => {
  const res = useMutation(
    (info: {
      org_id: string;
      org_avatar_url: string;
      org_avatar_thumbnail_url: string;
      _tx?: L2TxReqParams;
    }) =>
      request.post<{ success: boolean }>('/org/apply_verify', {
        ...info,
      }),
  );

  return res;
};

export const useRemoveOrgApplyPost = () => {
  const res = useMutation(() => request.post<{ success: boolean }>('/org/remove_verify', {}));
  return res;
};
